import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Button, Group, Stack, Text, Textarea } from "@mantine/core"
import { modals } from "@mantine/modals"
import { SettingsReadOnlyNotice } from "../SettingsReadOnlyNotice"

interface IProps {
  settings: Record<string, string>
  saving: boolean
  /** Owner/admin (or super-admin) may edit; a plain member gets read-only. Default true. */
  canManage?: boolean
  updateSetting: (key: string, value: string) => void
  save: (keys?: string[]) => void
}

export function PromptPanel({ settings, saving, canManage = true, updateSetting, save }: IProps): React.JSX.Element {
  const { i18n } = useLingui()

  // Reset clears the custom prompt back to the built-in default — destructive
  // and previously a one-click wipe. Confirm first so an accidental click can't
  // discard a carefully tuned prompt.
  const confirmReset = (): void => {
    modals.openConfirmModal({
      title: i18n._(msg`Reset to the default prompt?`),
      children: <Text size="sm">{i18n._(msg`Your custom prompt will be discarded.`)}</Text>,
      labels: { confirm: i18n._(msg`Reset`), cancel: i18n._(msg`Cancel`) },
      confirmProps: { color: "danger" },
      onConfirm: () => updateSetting("system_prompt", ""),
    })
  }

  return (
    <Stack gap="md">
      <Textarea
        label={i18n._(msg`Default system prompt`)}
        value={settings.system_prompt ?? ""}
        onChange={(e) => updateSetting("system_prompt", e.currentTarget.value)}
        minRows={16}
        autosize
        disabled={!canManage}
        styles={{ input: { fontFamily: "var(--mantine-font-family-monospace)", fontSize: "0.8rem" } }}
      />
      {canManage ? (
        <Group>
          <Button onClick={() => save(["system_prompt"])} loading={saving}>
            <Trans>Save prompt</Trans>
          </Button>
          <Button variant="subtle" color="gray" onClick={confirmReset}>
            <Trans>Reset to default</Trans>
          </Button>
        </Group>
      ) : (
        <SettingsReadOnlyNotice />
      )}
    </Stack>
  )
}
